import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Users } from "lucide-react"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { IdentityTab } from "@/components/admin/IdentityTab"
import { MasterDataSection, type Column } from "@/components/admin/MasterDataSection"
import { TeamMembersSheet } from "@/components/admin/TeamMembersSheet"
import { useMasterData } from "@/components/admin/useMasterData"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useCan } from "@/lib/permissions"
import { api } from "@/services/api"
import type {
  AssignmentType,
  AssignmentTypeCreate,
  CostCenter,
  CostCenterCreate,
  JobDescription,
  JobDescriptionCreate,
  Schichttyp,
  Shift,
  ShiftCreate,
  ShiftModel,
  ShiftModelCreate,
  Team,
  TeamCreate,
} from "@/types"

const SHIFT_TYPE_LABEL: Record<Schichttyp, string> = {
  T: "Tag",
  FS: "Früh/Spät",
  FNS: "Früh/Nacht/Spät",
}

// =============================================================================
// Tab-Komponenten
// =============================================================================

function TeamsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<Team, TeamCreate>("teams", api.organization.teams, "Team")
  const shiftModels = useQuery({
    queryKey: ["shift-models"],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
  })
  const [membersTeam, setMembersTeam] = useState<Team | null>(null)

  const modelName = (id: string | null) =>
    id ? (shiftModels.data?.data.find((m) => m.id === id)?.name ?? "—") : "—"

  const columns: Column<Team>[] = [
    { header: "Name", cell: (t) => <span className="font-medium">{t.name}</span> },
    { header: "Schichtmodell", cell: (t) => modelName(t.shiftModelId) },
    {
      header: "Rotation",
      className: "text-right tabular-nums",
      cell: (t) => t.rotationOrder,
    },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    {
      key: "shiftModelId",
      label: "Schichtmodell",
      kind: "select",
      emptyOption: "— kein Schichtmodell —",
      options: (shiftModels.data?.data ?? []).map((m) => ({ value: m.id, label: m.name })),
    },
    { key: "rotationOrder", label: "Rotations-Reihenfolge", kind: "number", required: true },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-teams"
        emptyText="Keine Teams vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neues Team"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
        rowActions={(row) => (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Besetzung anzeigen"
            title="Besetzung"
            onClick={() => setMembersTeam(row)}
          >
            <Users className="!size-3.5" />
          </Button>
        )}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Team bearbeiten" : "Neues Team"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          shiftModelId: crud.dialog.row?.shiftModelId ?? "",
          rotationOrder: crud.dialog.row ? String(crud.dialog.row.rotationOrder) : "1",
        }}
        pending={crud.save.isPending}
        onSubmit={(v: FormValues) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              shiftModelId: v.shiftModelId ? Number(v.shiftModelId) : null,
              rotationOrder: Number(v.rotationOrder),
            },
          })
        }
      />
      <TeamMembersSheet
        team={membersTeam}
        onClose={() => setMembersTeam(null)}
        canWrite={canWrite}
      />
    </>
  )
}

function ShiftModelsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<ShiftModel, ShiftModelCreate>(
    "shift-models",
    api.organization.shiftModels,
    "Schichtmodell"
  )

  const columns: Column<ShiftModel>[] = [
    { header: "Name", cell: (m) => <span className="font-medium">{m.name}</span> },
    { header: "Schichttyp", cell: (m) => SHIFT_TYPE_LABEL[m.shiftType] ?? m.shiftType },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    {
      key: "shiftType",
      label: "Schichttyp",
      kind: "select",
      required: true,
      options: (Object.keys(SHIFT_TYPE_LABEL) as Schichttyp[]).map((t) => ({
        value: t,
        label: `${SHIFT_TYPE_LABEL[t]} (${t})`,
      })),
    },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-schichtmodelle"
        emptyText="Keine Schichtmodelle vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neues Schichtmodell"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Schichtmodell bearbeiten" : "Neues Schichtmodell"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          shiftType: crud.dialog.row?.shiftType ?? "T",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: { name: String(v.name).trim(), shiftType: v.shiftType as Schichttyp },
          })
        }
      />
    </>
  )
}

function ShiftsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<Shift, ShiftCreate>("shifts", api.organization.shifts, "Schicht")
  const shiftModels = useQuery({
    queryKey: ["shift-models"],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
  })
  const teams = useQuery({
    queryKey: ["teams"],
    queryFn: () => api.organization.teams.list({ size: 100 }),
  })

  const modelName = (id: string) => shiftModels.data?.data.find((m) => m.id === id)?.name ?? "—"
  const teamName = (id: string | null) =>
    id ? (teams.data?.data.find((t) => t.id === id)?.name ?? "—") : "—"
  const fmtTime = (t: string | null) => (t ? t.slice(0, 5) : "—")

  const columns: Column<Shift>[] = [
    { header: "Name", cell: (s) => <span className="font-medium">{s.name}</span> },
    { header: "Schichtmodell", cell: (s) => modelName(s.shiftModelId) },
    { header: "Rotation", className: "text-right tabular-nums", cell: (s) => s.rotationOrder },
    {
      header: "Zeiten",
      className: "tabular-nums",
      cell: (s) => `${fmtTime(s.startsAt)} – ${fmtTime(s.endsAt)}`,
    },
    { header: "Startteam", cell: (s) => teamName(s.startTeamId) },
    {
      header: "Nachtschicht",
      cell: (s) =>
        s.isNightShift ? <Badge variant="secondary">Nacht</Badge> : <span>—</span>,
    },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    {
      key: "shiftModelId",
      label: "Schichtmodell",
      kind: "select",
      required: true,
      emptyOption: "— wählen —",
      options: (shiftModels.data?.data ?? []).map((m) => ({ value: m.id, label: m.name })),
    },
    { key: "rotationOrder", label: "Rotations-Reihenfolge", kind: "number", required: true },
    { key: "startsAt", label: "Beginn", kind: "time" },
    { key: "endsAt", label: "Ende", kind: "time" },
    {
      key: "startTeamId",
      label: "Startteam",
      kind: "select",
      emptyOption: "— kein Startteam —",
      options: (teams.data?.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    },
    { key: "isNightShift", label: "Nachtschicht", kind: "switch" },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-schichten"
        emptyText="Keine Schichten vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Schicht"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Schicht bearbeiten" : "Neue Schicht"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          shiftModelId: crud.dialog.row?.shiftModelId ?? "",
          rotationOrder: crud.dialog.row ? String(crud.dialog.row.rotationOrder) : "1",
          startsAt: crud.dialog.row?.startsAt?.slice(0, 5) ?? "",
          endsAt: crud.dialog.row?.endsAt?.slice(0, 5) ?? "",
          startTeamId: crud.dialog.row?.startTeamId ?? "",
          isNightShift: crud.dialog.row?.isNightShift ?? false,
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              shiftModelId: Number(v.shiftModelId),
              rotationOrder: Number(v.rotationOrder),
              startsAt: v.startsAt ? String(v.startsAt) : null,
              endsAt: v.endsAt ? String(v.endsAt) : null,
              startTeamId: v.startTeamId ? Number(v.startTeamId) : null,
              isNightShift: v.isNightShift === true,
            },
          })
        }
      />
    </>
  )
}

function AssignmentTypesTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<AssignmentType, AssignmentTypeCreate>(
    "assignment-types",
    api.organization.assignmentTypes,
    "Beauftragungstyp"
  )

  const columns: Column<AssignmentType>[] = [
    { header: "Name", cell: (a) => <span className="font-medium">{a.name}</span> },
    { header: "Rechtsgrundlage", cell: (a) => a.legalBasis ?? "—" },
    {
      header: "Stabsstelle",
      cell: (a) =>
        a.isStaffPosition ? <Badge variant="secondary">Stabsstelle</Badge> : <span>—</span>,
    },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "staffDescription", label: "Beschreibung", kind: "textarea" },
    { key: "legalBasis", label: "Rechtsgrundlage", kind: "text" },
    { key: "link", label: "Link", kind: "text", placeholder: "https://…" },
    { key: "isStaffPosition", label: "Stabsstelle", kind: "switch" },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-beauftragungstypen"
        emptyText="Keine Beauftragungstypen vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neuer Beauftragungstyp"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Beauftragungstyp bearbeiten" : "Neuer Beauftragungstyp"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          staffDescription: crud.dialog.row?.staffDescription ?? "",
          legalBasis: crud.dialog.row?.legalBasis ?? "",
          link: crud.dialog.row?.link ?? "",
          isStaffPosition: crud.dialog.row?.isStaffPosition ?? false,
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              staffDescription: String(v.staffDescription).trim() || null,
              legalBasis: String(v.legalBasis).trim() || null,
              link: String(v.link).trim() || null,
              isStaffPosition: v.isStaffPosition === true,
            },
          })
        }
      />
    </>
  )
}

function CostCentersTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<CostCenter, CostCenterCreate>(
    "cost-centers",
    api.organization.costCenters,
    "Kostenstelle"
  )

  const columns: Column<CostCenter>[] = [
    { header: "Code", className: "tabular-nums", cell: (c) => c.code },
    { header: "Name", cell: (c) => <span className="font-medium">{c.name}</span> },
  ]

  const fields: FieldDef[] = [
    { key: "code", label: "Code", kind: "text", required: true, placeholder: "z. B. 3000" },
    { key: "name", label: "Name", kind: "text", required: true },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-kostenstellen"
        emptyText="Keine Kostenstellen vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Kostenstelle"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Kostenstelle bearbeiten" : "Neue Kostenstelle"}
        fields={fields}
        initialValues={{
          code: crud.dialog.row?.code ?? "",
          name: crud.dialog.row?.name ?? "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: { code: String(v.code).trim(), name: String(v.name).trim() },
          })
        }
      />
    </>
  )
}

function JobDescriptionsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<JobDescription, JobDescriptionCreate>(
    "job-descriptions",
    api.organization.jobDescriptions,
    "Stellenbeschreibung"
  )

  const columns: Column<JobDescription>[] = [
    { header: "Name", cell: (j) => <span className="font-medium">{j.name}</span> },
    { header: "Code", className: "tabular-nums", cell: (j) => j.code ?? "—" },
    { header: "Dokument-ID", className: "tabular-nums", cell: (j) => j.documentId ?? "—" },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "code", label: "Code", kind: "text" },
    { key: "documentId", label: "Dokument-ID (optional)", kind: "number" },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-stellenbeschreibungen"
        emptyText="Keine Stellenbeschreibungen vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Stellenbeschreibung"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-admin-masterdata"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Stellenbeschreibung bearbeiten" : "Neue Stellenbeschreibung"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          code: crud.dialog.row?.code ?? "",
          documentId: crud.dialog.row?.documentId ?? "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              code: String(v.code).trim() || null,
              documentId: v.documentId ? Number(v.documentId) : null,
            },
          })
        }
      />
    </>
  )
}

// =============================================================================
// Seite
// =============================================================================

export function AdminMasterDataPage() {
  const canWrite = useCan("personal_schreiben")
  const canDelete = useCan("loeschen")
  const isAdmin = useCan("admin")
  const [tab, setTab] = useState("teams")

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Stammdaten</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="schichtmodelle">Schichtmodelle</TabsTrigger>
          <TabsTrigger value="schichten">Schichten</TabsTrigger>
          <TabsTrigger value="beauftragungstypen">Beauftragungstypen</TabsTrigger>
          <TabsTrigger value="kostenstellen">Kostenstellen</TabsTrigger>
          <TabsTrigger value="stellenbeschreibungen">Stellenbeschreibungen</TabsTrigger>
          {isAdmin && <TabsTrigger value="identity">Identity</TabsTrigger>}
        </TabsList>
        <TabsContent value="teams">
          <TeamsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="schichtmodelle">
          <ShiftModelsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="schichten">
          <ShiftsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="beauftragungstypen">
          <AssignmentTypesTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="kostenstellen">
          <CostCentersTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="stellenbeschreibungen">
          <JobDescriptionsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="identity">
            <IdentityTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
